import moment from 'moment-timezone';

import { GLUCOSE_UNITS, TEMPERATURE_UNITS, WEIGHT_UNITS } from '../../../utils/unitConverters';
import { graphDataProvidersEnum } from './graphDataProvidersEnum';

// Vital (KPI) names as the backend gives them. Same strings as VITALS in utils/readings, which cannot be imported
// here without dragging in the app's import cycle (readings -> utils -> enrollmentFields -> readings).
const VITALS = {
  WEIGHT: 'weight',
  PRESSURE_SYSTOLIC: 'pressure_systolic',
  PRESSURE_DIASTOLIC: 'pressure_diastolic',
  MEAN_ARTERIAL_PRESSURE: 'mean_arterial_pressure',
  HEART_RATE: 'heart_rate',
  OXYGEN_SATURATION: 'oxygen_saturation',
  PERFUSION_INDEX: 'perfusion_index',
  SUGAR_LEVEL: 'sugar_level',
  TEMPERATURE: 'temperature',
  PROTHROMBIN_TIME: 'prothrombin_time',
  INTERNATIONAL_NORMALIZED_RATIO: 'international_normalized_ratio',
};

// Vitals each chart is drawn from. Charts of a patient's vitals read them by vital (KPI), whatever device or
// manual entry produced them; the rest (sleep, steps, spirometry, air quality, ...) are device-specific.
export const seriesKpis = {
  [graphDataProvidersEnum.weight]: [VITALS.WEIGHT],
  [graphDataProvidersEnum.bp]: [
    VITALS.PRESSURE_SYSTOLIC,
    VITALS.PRESSURE_DIASTOLIC,
    VITALS.MEAN_ARTERIAL_PRESSURE,
    VITALS.HEART_RATE,
  ],
  [graphDataProvidersEnum.hr]: [VITALS.HEART_RATE],
  [graphDataProvidersEnum.pulseox]: [VITALS.OXYGEN_SATURATION, VITALS.PERFUSION_INDEX],
  [graphDataProvidersEnum.gluco]: [VITALS.SUGAR_LEVEL],
  [graphDataProvidersEnum.temp]: [VITALS.TEMPERATURE],
  [graphDataProvidersEnum.ptInr]: [VITALS.PROTHROMBIN_TIME, VITALS.INTERNATIONAL_NORMALIZED_RATIO],
};

// Groups readings that came from one measurement (same event and time), e.g. systolic and diastolic pressure.
const groupMeasurements = points => {
  const groups = new Map();
  points.forEach(p => {
    const key = `${p.event_id}|${p.timestamp}`;
    if (!groups.has(key)) groups.set(key, { timestamp: p.timestamp, hidden: p.hidden, values: {} });
    groups.get(key).values[p.kpi] = p.value;
  });
  return [...groups.values()];
};

// Converts readings by vital into the item shape each chart's code below reads.
export const seriesToChartItems = {
  [graphDataProvidersEnum.weight]: points =>
    points.map(p => ({ event_timestamp: p.timestamp, weight: p.value, unit: WEIGHT_UNITS.kilogram, hidden: p.hidden })),
  [graphDataProvidersEnum.bp]: points =>
    groupMeasurements(points)
      .filter(g => g.values[VITALS.PRESSURE_SYSTOLIC] !== undefined || g.values[VITALS.PRESSURE_DIASTOLIC] !== undefined)
      .map(g => ({
        event_timestamp: g.timestamp,
        systolic_pressure: g.values[VITALS.PRESSURE_SYSTOLIC],
        diastolic_pressure: g.values[VITALS.PRESSURE_DIASTOLIC],
        mean_arterial_pressure: g.values[VITALS.MEAN_ARTERIAL_PRESSURE],
        pulse_rate: g.values[VITALS.HEART_RATE],
        hidden: g.hidden,
      })),
  [graphDataProvidersEnum.hr]: points =>
    points.map(p => ({ timestamp: moment(p.timestamp).unix(), heart_rate: p.value, hidden: p.hidden })),
  [graphDataProvidersEnum.pulseox]: points =>
    groupMeasurements(points)
      .filter(g => g.values[VITALS.OXYGEN_SATURATION] !== undefined)
      .map(g => ({
        timestamp: moment(g.timestamp).unix(),
        saturation: g.values[VITALS.OXYGEN_SATURATION],
        pi: g.values[VITALS.PERFUSION_INDEX],
        hidden: g.hidden,
      })),
  [graphDataProvidersEnum.gluco]: points =>
    points.map(p => ({
      timestamp: p.timestamp,
      glucose_concentration: p.value,
      glucose_unit: GLUCOSE_UNITS.mg_dl,
      hidden: p.hidden,
    })),
  [graphDataProvidersEnum.temp]: points =>
    points.map(p => ({
      timestamp: moment(p.timestamp).unix(),
      temperature: p.value,
      unit: TEMPERATURE_UNITS.c,
      hidden: p.hidden,
    })),
  [graphDataProvidersEnum.ptInr]: points =>
    groupMeasurements(points).map(g => ({
      timestamp: moment(g.timestamp).unix(),
      pt_value: g.values[VITALS.PROTHROMBIN_TIME],
      inr_value: g.values[VITALS.INTERNATIONAL_NORMALIZED_RATIO],
      hidden: g.hidden,
    })),
};

